import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CITY_STATE_MAP, MAJOR_CITIES, STATE_CITIES_MAP } from '../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) return logoUrl;
  return `${API}/files/${logoUrl}`;
};

const GoldInput = ({ label, required, ...props }) => (
  <div>
    <label className="block text-sm text-white/60 mb-2">{label}{required && <span className="text-[#C5A059] ml-1">*</span>}</label>
    <input {...props} className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#C5A059] focus:outline-none transition-colors placeholder:text-white/20" />
  </div>
);

const GoldButton = ({ children, variant = 'primary', ...props }) => {
  const base = variant === 'primary' ? 'bg-[#C5A059] text-black hover:bg-[#E2C382]' : 'border border-white/20 text-white hover:border-white/60';
  return <button {...props} className={`px-8 py-3 text-sm font-medium tracking-wide transition-colors disabled:opacity-50 ${base} ${props.className || ''}`}>{children}</button>;
};

function formatError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// ─── Provider Card ───
const ProviderCard = ({ provider, index, viewerVerified, onFeedback }) => {
  const p = provider;
  const i = index;
  return (
    <div className="bg-[#0F0F14] border border-white/[0.04] p-6 hover:border-white/[0.12] transition-colors" data-testid={`provider-card-${i}`}>
      <div className="flex items-start gap-4 mb-3">
        {p.logo_url && <img src={resolveLogoUrl(p.logo_url)} alt={`${p.name} logo`} className="w-24 h-24 object-cover border border-white/10 rounded" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
        <div className="flex-1">
          <h3 className="text-lg font-medium text-white" style={{ fontFamily: 'Cormorant Garamond' }}>{p.name}</h3>
          {p.specialty && <span className="text-xs uppercase tracking-wider text-[#C5A059]">{p.specialty}</span>}
        </div>
      </div>
      {p.city && <p className="text-white/50 text-xs mb-2">{p.city}{p.state ? `, ${p.state}` : ''}{p.location ? ` - ${p.location}` : ''}</p>}
      {p.discount_percent > 0 && <p className="text-[#C5A059] text-sm font-medium mb-2">{p.discount_percent}% Member Discount</p>}

      {/* Why Recommended */}
      {p.why_recommended && (
        <div className="bg-[#C5A059]/5 border border-[#C5A059]/20 px-4 py-3 mb-3" data-testid={`why-recommended-${i}`}>
          <div className="text-[10px] uppercase tracking-wider text-[#C5A059] mb-1">Why Recommended</div>
          <p className="text-white/70 text-sm">{p.why_recommended}</p>
        </div>
      )}

      {/* Trust Indicators */}
      {(p.years_of_experience || p.languages_spoken?.length > 0 || p.top_specialisations?.length > 0) && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid={`trust-indicators-${i}`}>
          {p.years_of_experience && <span className="text-xs bg-white/5 border border-white/10 px-2 py-1 text-white/60">{p.years_of_experience}+ years exp</span>}
          {p.languages_spoken?.map((l, idx) => <span key={`l-${idx}`} className="text-xs bg-white/5 border border-white/10 px-2 py-1 text-white/60">{l}</span>)}
          {p.top_specialisations?.map((s, idx) => <span key={`s-${idx}`} className="text-xs bg-[#C5A059]/10 border border-[#C5A059]/20 px-2 py-1 text-[#C5A059]/80">{s}</span>)}
        </div>
      )}

      {viewerVerified && (
        <>
          {p.bio && <p className="text-white/60 text-sm mb-3">{p.bio}</p>}
          <ContactSection provider={p} index={i} />
          {p.services_offered?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {p.services_offered.map((s, idx) => (
                <span key={`svc-${s}-${idx}`} className="text-xs bg-white/5 border border-white/10 px-2 py-1 text-white/60">{s}</span>
              ))}
            </div>
          )}
          <ActionButtons provider={p} index={i} onFeedback={onFeedback} />
        </>
      )}
    </div>
  );
};

// ─── Contact Section ───
const ContactSection = ({ provider: p, index: i }) => (
  <div className="border-t border-white/[0.04] pt-3 mt-3 space-y-1.5">
    {p.contact_person_number && (
      <div className="flex items-center gap-2">
        <span className="text-white/40 text-xs w-14">Phone:</span>
        <a href={`tel:${p.contact_person_number}`} className="text-white/70 text-xs hover:text-[#C5A059] transition-colors" data-testid={`provider-phone-${i}`}>{p.contact_person_number}</a>
      </div>
    )}
    {p.email && (
      <div className="flex items-center gap-2">
        <span className="text-white/40 text-xs w-14">Email:</span>
        <a href={`mailto:${p.email}`} className="text-white/70 text-xs hover:text-[#C5A059] transition-colors" data-testid={`provider-email-${i}`}>{p.email}</a>
      </div>
    )}
    {p.website && (
      <div className="flex items-center gap-2">
        <span className="text-white/40 text-xs w-14">Web:</span>
        <a href={p.website.startsWith('http') ? p.website : `https://${p.website}`} target="_blank" rel="noopener noreferrer" className="text-white/70 text-xs hover:text-[#C5A059] transition-colors" data-testid={`provider-web-${i}`}>{p.website}</a>
      </div>
    )}
  </div>
);

// ─── Action Buttons (WhatsApp + Feedback) ───
const ActionButtons = ({ provider: p, index: i, onFeedback }) => {
  const trackAndOpen = (provider) => {
    const phone = provider.contact_person_number.replace(/[^0-9]/g, '').replace(/^0+/, '91');
    window.open(`https://wa.me/${phone}`, '_blank');
    // Fire-and-forget tracking after opening window (avoids popup blocker)
    axios.post(`${API}/track/whatsapp-click`, {
      provider_id: provider.id,
      provider_name: provider.name
    }).catch(() => {});
  };

  return (
  <div className="mt-4 space-y-2">
    {p.contact_person_number && (
      <button onClick={() => trackAndOpen(p)}
        className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#1da851] transition-colors"
        data-testid={`whatsapp-provider-${i}`}>
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        WhatsApp Chat
      </button>
    )}
    <button data-testid={`feedback-provider-${i}`} onClick={() => onFeedback(p)} className="w-full border border-white/20 text-white px-4 py-2 text-sm font-medium hover:border-white/60 transition-colors">Member Feedback</button>
  </div>
  );
};

// ─── Feedback Modal ───
const FeedbackModal = ({ provider, onClose }) => {
  const [form, setForm] = useState({ comment: '', would_recommend: true });
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/feedback`, { ...form, provider_id: provider.id }, { withCredentials: true });
      setStatus('Feedback submitted! Thank you.');
      setTimeout(onClose, 2000);
    } catch (err) {
      setStatus(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-6" data-testid="feedback-modal">
      <div className="bg-[#0F0F14] border border-white/[0.06] p-8 w-full max-w-lg">
        <h3 className="text-2xl font-light text-white mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>Member Feedback: {provider.name}</h3>
        {status ? (
          <div className="p-4 bg-[#C5A059]/10 text-[#C5A059] text-sm mb-4">{status}</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-white/60 mb-2">Share your experience</label>
              <textarea data-testid="feedback-comment" value={form.comment} onChange={e => setForm({...form, comment: e.target.value})} className="w-full bg-transparent border border-white/20 py-3 px-4 text-white focus:border-[#C5A059] focus:outline-none h-24 resize-none" required placeholder="How was your experience with this provider?" />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-2">Would you recommend?</label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                  <input type="radio" name="recommend" checked={form.would_recommend === true} onChange={() => setForm({...form, would_recommend: true})} className="accent-[#C5A059]" data-testid="recommend-yes" /> Yes
                </label>
                <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                  <input type="radio" name="recommend" checked={form.would_recommend === false} onChange={() => setForm({...form, would_recommend: false})} className="accent-[#C5A059]" data-testid="recommend-no" /> No
                </label>
              </div>
            </div>
            <div className="flex gap-3">
              <GoldButton data-testid="feedback-submit" type="submit">Submit Feedback</GoldButton>
              <GoldButton variant="secondary" type="button" onClick={onClose}>Cancel</GoldButton>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ─── Category Filters ───
const CategoryFilters = ({ categories, selectedCat, onSelect }) => (
  <div className="flex flex-wrap gap-3 mb-6" data-testid="category-tabs">
    {categories.map(cat => (
      <button key={cat.id} data-testid={`category-tab-${cat.id}`} onClick={() => onSelect(cat.id)} className={`px-6 py-3 text-sm font-medium tracking-wide transition-colors ${selectedCat === cat.id ? 'bg-[#C5A059] text-black' : 'border border-white/20 text-white hover:border-white/60'}`}>
        {cat.name}
      </button>
    ))}
  </div>
);

// ─── Location Filters ───
const LocationFilters = ({ filterCity, filterState, availableCities, availableStates, onCityChange, onStateChange, onClear }) => (
  <div className="flex flex-wrap gap-4 mb-8 items-end" data-testid="location-filters">
    <div>
      <label className="block text-xs text-white/40 mb-1">Filter by City</label>
      <select data-testid="filter-city" value={filterCity} onChange={e => onCityChange(e.target.value)} className="bg-[#0F0F14] border border-white/20 py-2 px-4 text-white text-sm focus:border-[#C5A059] focus:outline-none min-w-[160px]">
        <option value="">All Cities</option>
        {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
    <div>
      <label className="block text-xs text-white/40 mb-1">Filter by State</label>
      <select data-testid="filter-state" value={filterState} onChange={e => onStateChange(e.target.value)} className="bg-[#0F0F14] border border-white/20 py-2 px-4 text-white text-sm focus:border-[#C5A059] focus:outline-none min-w-[160px]">
        <option value="">All States</option>
        {availableStates.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
    {(filterCity || filterState) && (
      <button data-testid="clear-filters-btn" onClick={onClear} className="text-[#C5A059] text-xs hover:text-[#E2C382] py-2">Clear Filters</button>
    )}
  </div>
);

// ─── Subscription Paywall ───
const SubscriptionPaywall = () => (
  <div className="bg-[#0F0F14] border border-[#C5A059]/30 p-8 md:p-12 text-center" data-testid="subscription-paywall">
    <h3 className="text-2xl font-light text-white mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>Subscription Required</h3>
    <p className="text-white/60 text-sm mb-6 max-w-md mx-auto">Subscribe to unlock access to our exclusive service provider directory.</p>
    <div className="mb-6">
      <span className="text-4xl font-light text-[#C5A059]" style={{ fontFamily: 'Cormorant Garamond' }}>{'\u20B9'}399</span>
      <span className="text-white/40 text-sm ml-2">/ month</span>
    </div>
    <p className="text-white/40 text-xs">Contact admin to activate your subscription</p>
  </div>
);

// ═══════════════════════════════════════════
// MAIN SERVICES PAGE (composed from sub-components)
// ═══════════════════════════════════════════

export const ServicesPage = ({ Header }) => {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const initialCat = searchParams.get('cat') || '';
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');
  const [providers, setProviders] = useState([]);
  const [viewerVerified, setViewerVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [feedbackProvider, setFeedbackProvider] = useState(null);
  const [filterCity, setFilterCity] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterService, setFilterService] = useState(initialSearch);
  const [serviceInput, setServiceInput] = useState(initialSearch);
  const [availableCities, setAvailableCities] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);

  useEffect(() => {
    axios.get(`${API}/categories`, { withCredentials: true })
      .then(({ data }) => { setCategories(data); if (initialCat) setSelectedCat(initialCat); else if (data.length && !initialSearch) setSelectedCat(data[0].id); })
      .catch(() => {});
    axios.get(`${API}/service-providers/filters`, { withCredentials: true })
      .then(({ data }) => { setAvailableCities(data.cities || []); setAvailableStates(data.states || []); })
      .catch(() => {});
  }, [initialSearch, initialCat]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedCat && !filterService) params.append('category_id', selectedCat);
    if (filterCity) params.append('city', filterCity);
    if (filterState) params.append('state', filterState);
    if (filterService) params.append('service', filterService);
    axios.get(`${API}/service-providers?${params.toString()}`, { withCredentials: true })
      .then(({ data }) => { setProviders(data.providers); setViewerVerified(data.viewer_verified); setSubscriptionRequired(data.subscription_required || false); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedCat, filterCity, filterState, filterService]);

  // Merge major cities with available cities from providers
  const allCities = [...new Set([...MAJOR_CITIES, ...availableCities])].sort();

  const handleCityChange = (city) => {
    setFilterCity(city);
    if (CITY_STATE_MAP[city]) setFilterState(CITY_STATE_MAP[city]);
  };

  return (
    <div className="min-h-screen bg-[#05050A]" style={{ fontFamily: 'Outfit' }}>
      <Header />
      <main className="pt-24 px-6 md:px-12 lg:px-24 py-12">
        <h1 className="text-4xl lg:text-5xl font-light text-white mb-8" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="services-heading">Trusted Partners</h1>

        {/* Prominent Search Box with City/State */}
        <div className="bg-[#0F0F14] border border-white/[0.08] p-8 mb-10" data-testid="expert-search-box">
          <h2 className="text-xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>Looking for a trusted professional?</h2>
          <p className="text-white/40 text-sm mb-5">Search by service, profession or describe your requirement</p>

          {/* State & City filters inside search box */}
          <div className="flex flex-wrap gap-4 mb-5">
            <div>
              <label className="block text-xs text-white/40 mb-1">State</label>
              <select data-testid="filter-state" value={filterState} onChange={e => { setFilterState(e.target.value); setFilterCity(''); }} className="bg-[#0F0F14] border border-white/20 py-2.5 px-4 text-white text-sm focus:border-[#C5A059] focus:outline-none min-w-[180px]">
                <option value="">All States</option>
                {[...new Set([...availableStates, 'Haryana'])].sort().map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">City</label>
              <select data-testid="filter-city" value={filterCity} onChange={e => handleCityChange(e.target.value)} className="bg-[#0F0F14] border border-white/20 py-2.5 px-4 text-white text-sm focus:border-[#C5A059] focus:outline-none min-w-[180px]">
                <option value="">All Cities</option>
                {(filterState ? (STATE_CITIES_MAP[filterState] || []) : allCities).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {(filterCity || filterState) && (
              <button data-testid="clear-location-btn" onClick={() => { setFilterCity(''); setFilterState(''); }} className="text-[#C5A059] text-xs hover:text-[#E2C382] self-end py-2.5">Clear</button>
            )}
          </div>

          <div className="flex gap-3">
            <input data-testid="expert-search-input" value={serviceInput} onChange={e => setServiceInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setFilterService(serviceInput); setSelectedCat(''); } }}
              placeholder="e.g. Tax Consultant, Interior Designer, Wellness Coach..."
              className="flex-1 bg-transparent border border-white/20 py-3.5 px-5 text-white text-sm focus:border-[#C5A059] focus:outline-none placeholder:text-white/25 transition-colors" />
            <button data-testid="expert-search-btn" onClick={() => { setFilterService(serviceInput); setSelectedCat(''); }}
              className="bg-[#C5A059] text-black px-8 py-3.5 text-sm font-medium tracking-wide hover:bg-[#E2C382] transition-colors whitespace-nowrap">
              Find My Expert
            </button>
          </div>
        </div>

        <p className="text-white/40 text-sm mb-4 uppercase tracking-[0.15em]">Or browse by category</p>
        <CategoryFilters categories={categories} selectedCat={selectedCat} onSelect={(cat) => { setSelectedCat(cat); setFilterService(''); setServiceInput(''); }} />

        {!viewerVerified && !subscriptionRequired && (
          <div className="mb-6 p-4 border border-[#C5A059]/30 bg-[#C5A059]/5 text-[#C5A059] text-sm" data-testid="upgrade-notice">Subscribe to unlock full provider details and direct contact information.</div>
        )}

        {subscriptionRequired ? <SubscriptionPaywall /> : loading ? <p className="text-white/60">Loading...</p> : providers.length === 0 ? (
          <p className="text-white/60" data-testid="no-providers-message">No providers match your filters.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="providers-grid">
            {providers.map((p, i) => (
              <ProviderCard key={p.id || i} provider={p} index={i} viewerVerified={viewerVerified} onFeedback={setFeedbackProvider} />
            ))}
          </div>
        )}
      </main>

      {feedbackProvider && <FeedbackModal provider={feedbackProvider} onClose={() => setFeedbackProvider(null)} />}
    </div>
  );
};
